import { AlertForm, CancelSubmitForm, ConfirmDeleteForm, TextEditForm } from "@/components/form"
import { computed, type ComputedRef } from "vue"
import {
	Alert,
	ConfirmResult,
	createPopups,
	renderSlot,
	type AlertPopup,
	type Component,
	type ConfirmPopup,
	type Popup,
	type PopupContentProps,
	type PopupsComposable,
} from "vue-utils"
import type { JSX } from "vue/jsx-runtime"
import type { Nullable } from "../models"

export { ConfirmResult }
export type { AlertPopup, ConfirmPopup, Popup, PopupContentProps }

export interface PromptPopupOptions {
	initialValue?: string
	cancelText?: string
	okText?: string
}

export interface ConfirmDeletePopupOptions {
	title: string
	content: Component

	cancelText?: string
	deleteText?: string
}

export interface TaxRebatesPopups extends PopupsComposable {
	topPopup: ComputedRef<Nullable<Popup>>

	prompt(title: string, extraOptions?: PromptPopupOptions): Promise<string | null>
	alertMessage(title: string, message: string | JSX.Element): Promise<void>

	confirmDelete(entityName: string): Promise<boolean>
	confirmDelete(options: ConfirmDeletePopupOptions): Promise<boolean>
}

function initPopups(): TaxRebatesPopups {
	const popupsData = createPopups({
		AlertPopup: AlertForm,
		ConfirmPopup: ({ cancelText, onCancel, confirmText, onSubmit }, { slots }) => (
			<CancelSubmitForm onCancel={onCancel} cancelText={cancelText} onSubmit={onSubmit} submitText={confirmText}>
				{renderSlot(slots)}
			</CancelSubmitForm>
		),
	})

	const topPopup = computed<Nullable<Popup>>(() => {
		const popups = popupsData.popups.value
		return popups.length === 0 ? null : popups[popups.length - 1]
	})

	const prompt: TaxRebatesPopups["prompt"] = (title: string, extraOptions = {}) => {
		const { initialValue = "", cancelText = "Cancel", okText = "Ok" } = extraOptions
		return new Promise((resolve) =>
			popupsData.openPopup({
				onClose: () => {
					resolve(null)
				},
				content: (popupProps) => (
					<Alert title={title}>
						<TextEditForm
							initialValue={initialValue}
							cancel={() => {
								popupProps.close()
								resolve(null)
							}}
							onSubmit={(value) => {
								popupProps.close()
								resolve(value)
							}}
							cancelText={cancelText}
							okText={okText}
						/>
					</Alert>
				),
			})
		)
	}

	async function alertMessage(title: string, message: string | JSX.Element) {
		return await popupsData.showAlertPopup(() => <Alert title={title}>{message}</Alert>)
	}

	async function confirmDelete(options: string | ConfirmDeletePopupOptions): Promise<boolean> {
		if (typeof options === "string") {
			const entityName = options
			return await confirmDelete({
				title: "Confirm Delete",
				content: () => `Are you sure you want to delete this ${entityName}?`,
			})
		}
		return new Promise((resolve) => {
			popupsData.openPopup({
				onClose: () => {
					resolve(false)
				},
				content: (popupProps) => (
					<Alert title={options.title}>
						<ConfirmDeleteForm
							onCancel={() => {
								popupProps.close()
								resolve(false)
							}}
							onDelete={() => {
								popupProps.close()
								resolve(true)
							}}
							cancelText={options.cancelText}
							deleteText={options.deleteText}
						>
							<options.content />
						</ConfirmDeleteForm>
					</Alert>
				),
			})
		})
	}

	return {
		...popupsData,
		topPopup,
		alertMessage,
		prompt,
		confirmDelete,
	}
}

const popups = initPopups()
export default function usePopups(): TaxRebatesPopups {
	return popups
}
